import { RefObject, useCallback, useEffect, useRef } from 'react';
import { ItemCallback } from 'react-grid-layout';

const EDGE_PX = 60;
const MAX_SPEED_PX = 15;

type DragEventLike = MouseEvent | TouchEvent;

const getClientY = (event: DragEventLike): number | null => {
    if ('touches' in event) {
        return event.touches[0]?.clientY ?? null;
    }
    return event.clientY;
};

const computeScrollDelta = (pointerY: number, top: number, bottom: number): number => {
    const distFromTop = pointerY - top;
    if (distFromTop < EDGE_PX) {
        const intensity = Math.min(1, Math.max(0, 1 - distFromTop / EDGE_PX));
        return -MAX_SPEED_PX * intensity;
    }
    const distFromBottom = bottom - pointerY;
    if (distFromBottom < EDGE_PX) {
        const intensity = Math.min(1, Math.max(0, 1 - distFromBottom / EDGE_PX));
        return MAX_SPEED_PX * intensity;
    }
    return 0;
};

export type UseDragAutoScrollReturn = {
    onDrag: ItemCallback;
    onDragStop: ItemCallback;
};

// Edge-triggered auto-scroll for react-grid-layout drags. The library leaves the
// surrounding scroll container alone, so trackpad users can't reach rows below the
// viewport. We track the pointer Y from the drag event and continuously scroll the
// container while the pointer sits inside the EDGE_PX zone — independent of mouse
// movement, so holding the cursor near the edge keeps scrolling.
export const useDragAutoScroll = (scrollContainerRef: RefObject<HTMLElement>): UseDragAutoScrollReturn => {
    const pointerYRef = useRef<number | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const isActiveRef = useRef(false);

    const stop = useCallback(() => {
        isActiveRef.current = false;
        if (rafIdRef.current !== null) {
            window.cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        pointerYRef.current = null;
    }, []);

    const tick = useCallback(() => {
        if (!isActiveRef.current) {
            rafIdRef.current = null;
            return;
        }
        const container = scrollContainerRef.current;
        const pointerY = pointerYRef.current;
        if (container && pointerY !== null) {
            const { top, bottom } = container.getBoundingClientRect();
            const delta = computeScrollDelta(pointerY, top, bottom);
            if (delta !== 0) {
                container.scrollBy(0, delta);
            }
        }
        rafIdRef.current = window.requestAnimationFrame(tick);
    }, [scrollContainerRef]);

    const onDrag = useCallback<ItemCallback>(
        (_layout, _old, _new, _placeholder, event) => {
            const y = getClientY(event as DragEventLike);
            if (y !== null) pointerYRef.current = y;
            if (!isActiveRef.current) {
                isActiveRef.current = true;
                if (rafIdRef.current === null) {
                    rafIdRef.current = window.requestAnimationFrame(tick);
                }
            }
        },
        [tick]
    );

    const onDragStop = useCallback<ItemCallback>(() => stop(), [stop]);

    useEffect(() => stop, [stop]);

    return { onDrag, onDragStop };
};

export default useDragAutoScroll;
